<script setup lang="ts">
import FormElement from '@/components/FormElement.vue'
import InputSuffix from '@/components/InputSuffix.vue'
import { ValidatorType } from '@/helper/ValidateDirective'
import { ReferenceFuelType } from '@/helper/Types'
import CalculationResultBadge from '@/components/steps/planning/CalculationResultBadge.vue'
import ProjectController from '@/controllers/ProjectController'
import i18n, { formatLanguage } from '@/helper/Translations'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ComboBox from '@/components/ComboBox.vue'
import { computed, onMounted, ref } from 'vue'
import userStore from '@/store/UserStore'
import formStore from '@/store/FormStore'
import projectStore from '@/store/ProjectStore'

const props = defineProps<{
  validator: ValidatorType
}>()

defineExpose({
  validator: props.validator
})

const calculatedHeatingLoad = ref<string>('')

let heatingDebounceTimer: ReturnType<typeof setTimeout> | null = null

onMounted((): void => {
  updateHeatingLoad()
})

const calorificValue = computed(() => {
  const previousFuelType = formStore.data.previousFuelType as string
  const annualConsumptionUnit = formStore.data.annualConsumptionUnit as string

  if (!previousFuelType || !annualConsumptionUnit) {
    return undefined
  }

  return userStore.getCalorificValue(previousFuelType, annualConsumptionUnit)
})

const calorificValueInfo = computed(() => {
  const value = calorificValue.value?.toLocaleString(formatLanguage(), { maximumFractionDigits: 2 })

  return `${i18n.global.t('step_heating_details.previous_fueltype_calorific_value')} ${value} kWh/${
    formStore.data.annualConsumptionUnit
  }`
})

const selectedHeatingEfficiency = computed(() => {
  return (
    (userStore.referenceFuelTypes.find((type: ReferenceFuelType) => type.fuelType === formStore.data.previousFuelType)
      ?.heatingEfficiency as number) * 100 || ''
  )
})

const calorificValues = computed(() => {
  return userStore.getCalorificValues(formStore.data.previousFuelType as string).map(calorificValue => ({
    label: calorificValue,
    value: calorificValue
  }))
})

const fuelTypeOptions = computed(() => {
  return userStore.activeFuelTypeOptions.map(fuelType => ({
    label: i18n.global.t(`fuel_types.${fuelType}`),
    value: fuelType
  }))
})

const annualConsumptionUnit = computed({
  get() {
    return formStore.data.annualConsumptionUnit
  },
  set(unit) {
    if (formStore.data.annualConsumptionUnit !== unit) {
      formStore.data.annualConsumption = undefined
    }
    formStore.data.annualConsumptionUnit = unit
  }
})

function updateHeatingLoad(): void {
  if (heatingDebounceTimer) {
    clearTimeout(heatingDebounceTimer)
  }

  heatingDebounceTimer = setTimeout(() => {
    heatingDebounceTimer = null
    ProjectController.calculateHeatingLoad().then((heatingLoad: number | null) => {
      if (heatingLoad) {
        calculatedHeatingLoad.value = heatingLoad.toLocaleString(formatLanguage(), {
          maximumFractionDigits: 2
        })
      }
    })
  }, 500)
}

function previousFuelTypeChanged(): void {
  annualConsumptionUnit.value = Object.values(calorificValues.value)[0].value
  updateHeatingLoad()
}
</script>

<template>
  <section
    v-if="formStore.isHeating && userStore.enableHeatingConsumption"
    class="form__section has--line section--consumption-method"
  >
    <div class="form__section-inner has--line">
      <SectionHeadline class="form__title" translation-key="step_heating_details.title_heat_loss" />
      <div class="form__subsection">
        <div class="row form__row">
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.previousFuelType.formValidationRule"
              label-key="step_heating_details.previous_fueltype"
              :error="validator.heatingSystemPreviousFueltype?.error"
              :required="projectStore.formPropertyIsRequired('heatingLoss')"
            >
              <ComboBox
                id="heating-system-previous-fueltype"
                v-model="formStore.data.previousFuelType"
                :options="fuelTypeOptions"
                @change="previousFuelTypeChanged"
              />
            </FormElement>
            <p v-if="formStore.data.previousFuelType" class="form__info">
              {{ `${$t('step_heating_details.heating_system_efficiency')} ${selectedHeatingEfficiency}%` }}
            </p>
          </div>

          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.operatingHours.formValidationRule"
              label-key="step_heating_details.operating_hours"
              :error="validator.operatingHours?.error"
              :required="projectStore.formPropertyIsRequired('operatingHours')"
            >
              <InputSuffix
                id="operating-hours"
                key="operating-hours"
                v-model="formStore.data.operatingHours"
                class="inputsuffix__wrapper--full"
                suffix=" h"
                :placeholder="$t('step_heating_details.placeholder_operating_hours')"
                @change="updateHeatingLoad"
              />
            </FormElement>
          </div>
        </div>

        <div class="row form__row">
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.annualConsumptionUnit.formValidationRule"
              label-key="step_heating_details.annual_consumption_unit"
              :error="validator.annualConsumptionUnit?.error"
              :required="projectStore.formPropertyIsRequired('annualConsumptionUnit')"
            >
              <ComboBox
                id="annual-consumption-unit"
                v-model="annualConsumptionUnit"
                :disabled="!formStore.data.previousFuelType"
                :options="calorificValues"
                @change="updateHeatingLoad"
              />
            </FormElement>
            <p v-if="annualConsumptionUnit && calorificValue !== 1" class="form__info">
              {{ calorificValueInfo }}
            </p>
          </div>

          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.annualConsumption.formValidationRule"
              label-key="step_heating_details.annual_consumption"
              :error="validator.annualConsumption?.error"
              :required="projectStore.formPropertyIsRequired('annualConsumption')"
            >
              <InputSuffix
                id="annual-consumption"
                key="annual-consumption"
                v-model="formStore.data.annualConsumption"
                class="inputsuffix__wrapper--full"
                :suffix="' ' + annualConsumptionUnit"
                :placeholder="$t('step_heating_details.placeholder_annual_consumption')"
                @change="updateHeatingLoad"
              />
            </FormElement>
          </div>
        </div>
      </div>

      <CalculationResultBadge
        id="heating-calc-badge"
        icon-class="ic-ground"
        :calculated-load="calculatedHeatingLoad"
        :badge-label="$t('step_heating_details.badge_calculated_heat_loss_load')"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.section {
  &--consumption-method {
    .form__section-inner {
      padding-bottom: rem-calc(150px);
    }
  }
}
</style>
